// src/pages/SearchEngineDetails.tsx
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// --- Types ---
interface YLAFamilyDetails {
  FamilyCode: string;
  LoginID: string;
  Password: string;
  FatherName: string;
  MotherName: string;
  Surname: string;
  Address1: string;
  Address2: string;
  CITY: string;
  PostCode: string;
  Suburbcode: string;
  COUNTRY: string;
  Phone: string;
  DateOfSale: string;
  EMail: string;
  Active: string;
  InstallationDate: string;
  salesman: string;
  NoOfstudents: string;
  GSt: string;
  PST: string;
  HST: string;
}

interface SupportItem {
  Date: string;
  Description: string;
  FromField: string;
  ToField: string;
  ActionDate: string;
  Fixed: string;
  TypeAndReason: string;
  FromTv: string;
}

interface ELearnWebFamilyDetails {
  Password: string;
  FName: string;
  LName: string;
  Email: string;
  ActivityStatus: string;
  RegisteredDate: string;
  Address1: string;
  Address2: string;
  City: string;
  PostalCode: string;
  State: string;
  Country: string;
  AreaCode: string;
  Phone: string;
  Mobile: string;
  MaxChildren: string;
  FatherName: string;
  MotherName: string;
  FullName?: string;
}

// --- API Functions ---
const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const fetchWithTimeout = async (url: string, options: RequestInit = {}, timeout = 10000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (err) {
    clearTimeout(timeoutId);
    throw err;
  }
};

const fetchYLAFamilyDetails = async (familyCode: string) => {
  const response = await fetchWithTimeout(
    `${API_BASE_URL}api/yla-family-details/?family_code=${encodeURIComponent(familyCode)}`
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Failed to fetch YLA family details");
  }
  return response.json();
};

const fetchELearnWebFamilyDetails = async (schoolCode: string, familyCode: string, login: string) => {
  const response = await fetchWithTimeout(
    `${API_BASE_URL}api/elearnweb-family-details/?school_code=${encodeURIComponent(schoolCode)}&family_code=${encodeURIComponent(familyCode)}&login=${encodeURIComponent(login)}`
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Failed to fetch eLearnWeb family details");
  }
  return response.json();
};

// --- Component ---
const EL_SearchEngineDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // State for URL parameters
  const [type, setType] = useState<"YLA" | "eLearnWeb" | null>(null);
  const [familyCode, setFamilyCode] = useState<string>("");
  const [schoolCode, setSchoolCode] = useState<string>("");
  const [login, setLogin] = useState<string>("");

  // State for data
  const [ylaDetails, setYlaDetails] = useState<YLAFamilyDetails | null>(null);
  const [supportList, setSupportList] = useState<SupportItem[]>([]);
  const [elearnWebDetails, setELearnWebDetails] = useState<ELearnWebFamilyDetails | null>(null);
  const [clientID, setClientID] = useState<string>("");

  // State for UI
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  document.body.style.backgroundColor = "azure";

  // Parse query parameters from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const typeParam = searchParams.get("Type") as "YLA" | "eLearnWeb" | null;
    const familyCodeParam = searchParams.get("FamilyCode") || "";
    const schoolCodeParam = searchParams.get("SchoolCode") || "";
    const loginParam = searchParams.get("Login") || "";

    setType(typeParam);
    setFamilyCode(familyCodeParam);
    setSchoolCode(schoolCodeParam);
    setLogin(loginParam);
  }, [location.search]);

  // Fetch data based on type
  useEffect(() => {
    const fetchData = async () => {
      if (!type) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        if (type === "YLA" && familyCode) {
          const data = await fetchYLAFamilyDetails(familyCode);
          setYlaDetails(data.family_details);
          setSupportList(data.support_list || []);
        } else if (type === "eLearnWeb" && schoolCode && familyCode && login) {
          const data = await fetchELearnWebFamilyDetails(schoolCode, familyCode, login);
          setELearnWebDetails(data.family_details);
          setClientID(data.client_id || "");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err instanceof Error ? err.message : "Failed to load family details.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [type, familyCode, schoolCode, login]);

  // Handle closing the popup
  const handleClose = () => {
    if (window.parent && typeof (window.parent as any).gfn_HidePopup === 'function') {
      (window.parent as any).gfn_HidePopup();
    } else {
      navigate(-1); // Fallback: go back
    }
  };

  // Helper to render a value or "&nbsp;" if empty
  const renderValue = (value: string | undefined | null) => {
    return value ? value : <span>&nbsp;</span>;
  };

  // Loading state
  if (isLoading) {
    return (
      <div style={{
        textAlign: "center",
        padding: "50px",
        backgroundColor: "azure",
        fontFamily: "Arial",
        fontSize: "14pt"
      }}>
        Loading family details...
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        textAlign: "center",
        padding: "20px",
        backgroundColor: "azure",
        color: "red",
        fontFamily: "Arial"
      }}>
        <p>{error}</p>
        <button
          onClick={handleClose}
          style={{
            marginTop: "10px",
            padding: "8px 16px",
            backgroundColor: "#f0f0f0",
            border: "1px solid #ccc",
            cursor: "pointer"
          }}
        >
          Close
        </button>
      </div>
    );
  }

  // No data state
  if ((type === "YLA" && !ylaDetails) || (type === "eLearnWeb" && !elearnWebDetails)) {
    return (
      <div style={{
        textAlign: "center",
        padding: "20px",
        backgroundColor: "azure",
        fontFamily: "Arial"
      }}>
        <p>No family details found.</p>
        <button
          onClick={handleClose}
          style={{
            marginTop: "10px",
            padding: "8px 16px",
            backgroundColor: "#f0f0f0",
            border: "1px solid #ccc",
            cursor: "pointer"
          }}
        >
          Close
        </button>
      </div>
    );
  }

  // Main render
  return (
    <div style={{ backgroundColor: "azure", margin: 0, padding: "10px", fontFamily: "Arial" }}>
      <div style={{ textAlign: "center" }}>
        {/* YLA Family Details Section */}
        {type === "YLA" && ylaDetails && (
          <div id="divYLADB" style={{ width: "750px", margin: "0 auto", textAlign: "left" }}>
            <table
              cellPadding="5"
              cellSpacing="0"
              style={{
                width: "100%",
                fontFamily: "Arial",
                fontSize: "10pt",
                borderCollapse: "collapse"
              }}
            >
              <tbody>
                {/* Header */}
                <tr>
                  <td
                    colSpan={4}
                    style={{
                      textAlign: "center",
                      verticalAlign: "top",
                      fontSize: "12pt",
                      color: "white",
                      backgroundColor: "teal",
                      padding: "8px"
                    }}
                  >
                    <b>YLA Family Details</b>
                  </td>
                </tr>

                {/* Family Code and Phone */}
                <tr>
                  <td style={{ border: "1px solid black", borderRight: "none", width: "125px" }}>
                    <b>Family Code</b>
                  </td>
                  <td style={{ border: "1px solid black", borderRight: "none", width: "250px" }}>
                    {renderValue(ylaDetails.FamilyCode)}
                  </td>
                  <td style={{ border: "1px solid black", borderRight: "none", width: "125px" }}>
                    <b>Phone No</b>
                  </td>
                  <td style={{ border: "1px solid black", width: "250px" }}>
                    {renderValue(ylaDetails.Phone)}
                  </td>
                </tr>

                {/* Login ID and Date of Sale */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Login ID</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(ylaDetails.LoginID)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Date of Sale</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(ylaDetails.DateOfSale)}
                  </td>
                </tr>

                {/* Password and Email */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Password</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(ylaDetails.Password)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Email</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(ylaDetails.EMail)}
                  </td>
                </tr>

                {/* Father Name and Active */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Father Name</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(ylaDetails.FatherName)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Active</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(ylaDetails.Active === "1" ? "A" : ylaDetails.Active)}
                  </td>
                </tr>

                {/* Mother Name and Installed On */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Mother Name</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(ylaDetails.MotherName)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Installed On</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(ylaDetails.InstallationDate)}
                  </td>
                </tr>

                {/* Surname and Salesman */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Surname</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(ylaDetails.Surname)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Salesman</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(ylaDetails.salesman)}
                  </td>
                </tr>

                {/* Address and No of Students */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Address</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(`${ylaDetails.Address1}${ylaDetails.Address2 ? " <br /> " + ylaDetails.Address2 : ""}`)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>No of Students</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(ylaDetails.NoOfstudents)}
                  </td>
                </tr>

                {/* City and GST */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>City</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(ylaDetails.CITY)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>GST</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(ylaDetails.GSt)}
                  </td>
                </tr>

                {/* Post Code and PST */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Post Code</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(ylaDetails.PostCode)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>PST</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(ylaDetails.PST)}
                  </td>
                </tr>

                {/* Suburb Code and HST */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Suburb Code</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(ylaDetails.Suburbcode)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>HST</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(ylaDetails.HST)}
                  </td>
                </tr>

                {/* Country */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Country</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(ylaDetails.COUNTRY)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>&nbsp;</td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>&nbsp;</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Support List for YLA */}
        {type === "YLA" && supportList.length > 0 && (
          <div style={{ marginTop: "20px", width: "750px", marginLeft: "auto", marginRight: "auto" }}>
            <h3 style={{
              color: "white",
              backgroundColor: "teal",
              padding: "8px",
              margin: 0,
              fontFamily: "Arial",
              fontSize: "12pt"
            }}>
              Support List for {ylaDetails?.Surname} family from OLD YLA
            </h3>
            <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #ccc" }}>
              <table
                cellPadding="5"
                cellSpacing="0"
                style={{
                  width: "100%",
                  fontFamily: "Arial",
                  fontSize: "10pt",
                  borderCollapse: "collapse"
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f2f2f2" }}>
                    <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Date</th>
                    <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left", width: "500px" }}>Description</th>
                    <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>From</th>
                    <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>To</th>
                    <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Action Date</th>
                    <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Fixed</th>
                    <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Type & Reason</th>
                    <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>FromTv</th>
                  </tr>
                </thead>
                <tbody>
                  {supportList.map((item, index) => (
                    <tr key={index}>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>{renderValue(item.Date)}</td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>{renderValue(item.Description)}</td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>{renderValue(item.FromField)}</td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>{renderValue(item.ToField)}</td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>{renderValue(item.ActionDate)}</td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>{renderValue(item.Fixed)}</td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>{renderValue(item.TypeAndReason)}</td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>{renderValue(item.FromTv)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <br />

        {/* eLearnWeb Family Details Section */}
        {type === "eLearnWeb" && elearnWebDetails && (
          <div id="divELearnWebDB" style={{ width: "750px", margin: "0 auto", textAlign: "left" }}>
            <table
              cellPadding="5"
              cellSpacing="0"
              style={{
                width: "100%",
                fontFamily: "Arial",
                fontSize: "10pt",
                borderCollapse: "collapse"
              }}
            >
              <tbody>
                {/* Header */}
                <tr>
                  <td
                    colSpan={4}
                    style={{
                      textAlign: "center",
                      verticalAlign: "top",
                      fontSize: "12pt",
                      color: "red",
                      padding: "8px"
                    }}
                  >
                    <b>eLearnWeb Family Details</b>
                  </td>
                </tr>

                {/* School and Postal Code */}
                <tr>
                  <td style={{ border: "1px solid black", borderRight: "none", width: "125px" }}>
                    <b>School</b>
                  </td>
                  <td style={{ border: "1px solid black", borderRight: "none", width: "250px" }}>
                    {renderValue(schoolCode)}
                  </td>
                  <td style={{ border: "1px solid black", borderRight: "none", width: "250px" }}>
                    <b>Postal Code</b>
                  </td>
                  <td style={{ border: "1px solid black", width: "125px" }}>
                    {renderValue(elearnWebDetails.PostalCode)}
                  </td>
                </tr>

                {/* Login and State */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Login</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(login)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>State</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(elearnWebDetails.State)}
                  </td>
                </tr>

                {/* Password and Country */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Password</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(elearnWebDetails.Password)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Country</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(elearnWebDetails.Country)}
                  </td>
                </tr>

                {/* Name and Phone */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Name</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(elearnWebDetails.FullName)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Phone</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(elearnWebDetails.Phone)}
                  </td>
                </tr>

                {/* Email and Mobile */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Email</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(elearnWebDetails.Email)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Mobile</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(elearnWebDetails.Mobile)}
                  </td>
                </tr>

                {/* Activity Status and Father Name */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Activity Status</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(elearnWebDetails.ActivityStatus)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Father Name</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(elearnWebDetails.FatherName)}
                  </td>
                </tr>

                {/* Registered Date and Mother Name */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Registered Date</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(elearnWebDetails.RegisteredDate)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Mother Name</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(elearnWebDetails.MotherName)}
                  </td>
                </tr>

                {/* Address and Max Children */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Address</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(`${elearnWebDetails.Address1}${elearnWebDetails.Address2 ? " <br /> " + elearnWebDetails.Address2 : ""}`)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Max Children</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(elearnWebDetails.MaxChildren)}
                  </td>
                </tr>

                {/* City and Client ID */}
                <tr>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>City</b>
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    {renderValue(elearnWebDetails.City)}
                  </td>
                  <td style={{ borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                    <b>Client ID</b>
                  </td>
                  <td style={{ border: "1px solid black", borderTop: "none" }}>
                    {renderValue(clientID)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Close Button */}
        <div style={{ width: "750px", margin: "20px auto 0", textAlign: "center" }}>
          <button
            onClick={handleClose}
            style={{
              background: "url('/Images/close_d.gif') no-repeat center",
              width: "32px",
              height: "32px",
              border: "none",
              cursor: "pointer",
              backgroundSize: "contain",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundImage = "url('/Images/close_d.gif')";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundImage = "url('/Images/close_d.gif')";
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.backgroundImage = "url('/Images/close_d.gif')";
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default EL_SearchEngineDetails;